import React, { useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import AddAlt24 from "@carbon/icons-react/lib/add--alt/24.js";
import {
  Pagination,
  paginator,
  Content,
  EmployeesTable,
  EntryForm,
  ImportAssistant,
  Modal,
  PageLayout,
  scrollToElement,
  useTranslator,
} from "@sia/style-guide";
import GlobalContext from "contexts/GlobalContext";
import { DataCollectionContext } from "contexts/DataCollectionContext";
import { prepareEmployeesForTable } from "./utils";
import { EMPLOYEE_EXCEL_SCHEMA } from "./employeeExcelSchema";
import { PreviousSurveyEmployees } from "../../TestMode/PreviousSurveyData";
import "./EmployeesSection.scss";

const SurveyEmployeesSection = ({ employeeActions, onEmployeeCancel, onRequestOpenEmployeeForm, onRequestOpenFileUpload }) => {
  const translate = useTranslator();
  const { setIsLoaderShown, language, survey } = useContext(GlobalContext);
  const {
    showEmployeeEntryForm,
    employeeEntryFormItem,
    showEmployeesFileUpload,
    setShowEmployeesFileUpload,
    dropdownOptionsEmployees,
    employees,
    allEmployeeFunctions,
    OnReviewOrConcluded,
    isSurveyFinished,
  } = useContext(DataCollectionContext);
  const {
    setEmployeeFormDirty,
    employeeFormVersion,
    showDeleteEmployeeModal,
    employeeToDelete,
    isEmployeeMutationPending,
    isImportingEmployees,
    addOrUpdate,
    deleteEmployee,
    onCancelEmployeeConfirm,
    onDeleteEmployeeConfirm,
    importEmployees,
  } = employeeActions;

  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const isReadOnly = isSurveyFinished || OnReviewOrConcluded;
  const surveyYear = Number(survey?.surveyYear);
  const hasSurveyYear = Number.isInteger(surveyYear) && surveyYear >= 1000 && surveyYear <= 9999;
  const tableItems = useMemo(() => prepareEmployeesForTable(employees, dropdownOptionsEmployees), [dropdownOptionsEmployees, employees]);
  const totalPages = Math.max(1, Math.ceil(tableItems.length / itemsPerPage));
  const currentPage = Math.min(page, totalPages);
  const paginatedItems = useMemo(() => paginator.paginateItems(tableItems, currentPage, itemsPerPage), [currentPage, itemsPerPage, tableItems]);
  const guardPending =
    (action) =>
    (...args) =>
      isEmployeeMutationPending ? false : action(...args);
  const guardMutation =
    (action) =>
    (...args) =>
      isEmployeeMutationPending || !hasSurveyYear ? false : action(...args);
  const tableActions = isReadOnly
    ? [{ name: "view", onClick: (id) => onRequestOpenEmployeeForm({}, id) }]
    : [
        { name: "change", onClick: guardMutation((id) => onRequestOpenEmployeeForm({}, id)) },
        { name: "remove", onClick: guardMutation(deleteEmployee) },
      ];
  const excelDropdownOptions = { ...dropdownOptionsEmployees, specXFunc: dropdownOptionsEmployees?.allEmployeeFunctions };
  const firstErrorField = Object.keys(employeeEntryFormItem?.errors || {})[0];

  useEffect(() => {
    if (!showEmployeeEntryForm) return;
    const target = firstErrorField && employeeEntryFormItem?.hash ? `${firstErrorField.toLowerCase()}-error-msg` : "employee-entry-form";
    scrollToElement(target);
  }, [employeeEntryFormItem?.hash, firstErrorField, showEmployeeEntryForm]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    setIsLoaderShown(isImportingEmployees);
    return () => setIsLoaderShown(false);
  }, [isImportingEmployees, setIsLoaderShown]);

  return (
    <PageLayout>
      <div className="employees-data">
        {paginatedItems.length > 0 && (
          <>
            <div className="table-wrapper">
              <EmployeesTable
                items={paginatedItems}
                actions={tableActions}
                translate={translate}
                isDisabled={isReadOnly || isEmployeeMutationPending || !hasSurveyYear}
              />
            </div>
            <div className="sia-text-12 sia-padding-02-top">
              <Pagination
                items={tableItems}
                onChange={(nextPage, nextItemsPerPage) => {
                  setPage(nextPage);
                  setItemsPerPage(nextItemsPerPage);
                }}
                translate={translate}
                page={currentPage}
              />
            </div>
          </>
        )}
        {showDeleteEmployeeModal && employeeToDelete && (
          <Modal
            confirmLabel={translate("modal.button.confirm")}
            cancelLabel={translate("modal.button.cancel")}
            lead={translate("benchmarking.survey.employee.delete.confirm")}
            title={translate("modal.title.remove", { name: employeeToDelete.nameOrNum || "" })}
            onConfirm={onDeleteEmployeeConfirm}
            onCancel={onCancelEmployeeConfirm}
          />
        )}

        <div className={`${showEmployeesFileUpload ? "" : "action-buttons-closed"} sia-padding-05-bottom sia-padding-02-top`}>
          {!isReadOnly && hasSurveyYear && (
            <ImportAssistant
              translate={translate}
              onImportEmployees={importEmployees}
              isImporting={isImportingEmployees}
              showFileUpload={showEmployeesFileUpload}
              setShowFileUpload={setShowEmployeesFileUpload}
              openFileUpload={guardMutation(onRequestOpenFileUpload)}
              excelSchema={EMPLOYEE_EXCEL_SCHEMA}
              dropdownOptions={excelDropdownOptions}
              existingEmployees={employees}
              surveyYear={surveyYear}
            />
          )}

          {!showEmployeesFileUpload && !isReadOnly && hasSurveyYear && (
            <AddAlt24 onClick={guardMutation(() => onRequestOpenEmployeeForm({}, "blank"))} />
          )}
        </div>
        {showEmployeeEntryForm && hasSurveyYear && (
          <Content centeredTexts>
            <div key={`${employeeEntryFormItem?.employeeId ?? employeeEntryFormItem?.id ?? "new"}-${employeeFormVersion}`}>
              <EntryForm
                translate={translate}
                item={employeeEntryFormItem || {}}
                setHasBeenUpdated={setEmployeeFormDirty}
                submit={addOrUpdate}
                cancel={guardPending(onEmployeeCancel)}
                dropdownOptions={dropdownOptionsEmployees}
                allSpecXFunc={allEmployeeFunctions}
                isDisabled={isReadOnly || isEmployeeMutationPending || !hasSurveyYear}
                lang={language}
                surveyYear={surveyYear}
              />
            </div>
          </Content>
        )}
        <PreviousSurveyEmployees />
      </div>
    </PageLayout>
  );
};

SurveyEmployeesSection.propTypes = {
  employeeActions: PropTypes.shape({
    setEmployeeFormDirty: PropTypes.func.isRequired,
    employeeFormVersion: PropTypes.number.isRequired,
    showDeleteEmployeeModal: PropTypes.bool.isRequired,
    employeeToDelete: PropTypes.object,
    isEmployeeMutationPending: PropTypes.bool.isRequired,
    addOrUpdate: PropTypes.func.isRequired,
    deleteEmployee: PropTypes.func.isRequired,
    onCancelEmployeeConfirm: PropTypes.func.isRequired,
    onDeleteEmployeeConfirm: PropTypes.func.isRequired,
    importEmployees: PropTypes.func.isRequired,
    isImportingEmployees: PropTypes.bool.isRequired,
  }).isRequired,
  onEmployeeCancel: PropTypes.func.isRequired,
  onRequestOpenEmployeeForm: PropTypes.func.isRequired,
  onRequestOpenFileUpload: PropTypes.func.isRequired,
};

export default SurveyEmployeesSection;
